import React, { useEffect, useState } from 'react';
import Button from '../../../shared/components/ui/Button';
import { useForm } from 'react-hook-form';
import FormInput from '../../../shared/components/ui/FormInput';
import { useQuery } from '@tanstack/react-query';
import {
  getAllUsers,
  getPaymentByID,
  getPaymentsByIdStatic,
  getAllInvoices
} from '../../services/api/implementation/impl';
import { getAllCompanies } from '../../../builder/services/api/implementation/impl';
import { transformData } from '../../utils/transformData';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';
import { useProjectsList } from '../../../../contexts/ProjectsContext';

export const EditPayment = ({ onClose, onSubmit, id, isDisabled, staticPayments }) => {
  const [data, setData] = useState(null);
  const { projects, projectsForDropdown, isProjectsLoading } = useProjectsList();
  const [loading, setLoading] = useState(true);

  const userId = Cookies.get('userId');

  // Track form view
  useEffect(() => {
    mixpanel.track('Super Admin Edit Payment Form Viewed', {
      user_id: userId,
      payment_id: id,
      timestamp: new Date().toISOString()
    });
  }, [userId, id]);

  /* 
    Data fetching
  */
  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () => getAllUsers(),
    onSuccess: (result) => {
      // Check if result is an error object
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        mixpanel.track('Super Admin Edit Payment Users Fetch Failed', {
          user_id: userId,
          payment_id: id,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      mixpanel.track('Super Admin Edit Payment Users Fetch Success', {
        user_id: userId,
        payment_id: id,
        users_count: result?.data?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err) => {
      mixpanel.track('Super Admin Edit Payment Users Fetch Failed', {
        user_id: userId,
        payment_id: id,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const { data: companies, isLoading: companiesLoading } = useQuery({
    queryKey: ['companies'],
    queryFn: () => getAllCompanies(),
    onSuccess: (result) => {
      // Check if result is an error object
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        mixpanel.track('Super Admin Edit Payment Companies Fetch Failed', {
          user_id: userId,
          payment_id: id,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      mixpanel.track('Super Admin Edit Payment Companies Fetch Success', {
        user_id: userId,
        payment_id: id,
        companies_count: result?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err) => {
      mixpanel.track('Super Admin Edit Payment Companies Fetch Failed', {
        user_id: userId,
        payment_id: id,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const { data: invoices, isLoading: invoicesLoading } = useQuery({
    queryKey: ['invoices'],
    queryFn: () => getAllInvoices(),
    onSuccess: (result) => {
      // Check if result is an error object
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        mixpanel.track('Super Admin Edit Payment Invoices Fetch Failed', {
          user_id: userId,
          payment_id: id,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      mixpanel.track('Super Admin Edit Payment Invoices Fetch Success', {
        user_id: userId,
        payment_id: id,
        invoices_count: result?.length || 0,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err) => {
      mixpanel.track('Super Admin Edit Payment Invoices Fetch Failed', {
        user_id: userId,
        payment_id: id,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  const { data: payment, isLoading: paymentLoading } = useQuery({
    queryKey: ['payment', id],
    queryFn: () => getPaymentByID(id),
    enabled: !!id, // Only fetch payment if id is provided
    onSuccess: (result) => {
      // Check if result is an error object
      if (
        result?.response?.status ||
        result?.message?.includes('Error') ||
        result?.name === 'AxiosError'
      ) {
        mixpanel.track('Super Admin Edit Payment Data Fetch Failed', {
          user_id: userId,
          payment_id: id,
          error_message: result?.response?.data?.message || result?.message || 'Unknown error',
          timestamp: new Date().toISOString()
        });
        return;
      }

      mixpanel.track('Super Admin Edit Payment Data Fetch Success', {
        user_id: userId,
        payment_id: id,
        invoice_id: result?.[0]?.invoice_id,
        amount: result?.[0]?.amount,
        timestamp: new Date().toISOString()
      });
    },
    onError: (err) => {
      mixpanel.track('Super Admin Edit Payment Data Fetch Failed', {
        user_id: userId,
        payment_id: id,
        error_message: err?.response?.data?.message || err?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  });

  useEffect(() => {
    if (id) {
      const paymentData = getPaymentsByIdStatic(id, staticPayments);
      setData(paymentData);
    }
  }, [id, staticPayments]);

  /*
    Hook form
  */
  const { register, handleSubmit, setValue } = useForm();
  console.log('payment: ', payment);

  useEffect(() => {
    if (
      !usersLoading &&
      !companiesLoading &&
      !invoicesLoading &&
      !isProjectsLoading &&
      !paymentLoading
    ) {
      if (payment && payment[0]) {
        setValue('builder_id', payment[0]?.invoice?.builder?.id);
        setValue('company_id', payment[0]?.invoice?.company?.id);
        setValue('project_id', payment[0]?.invoice?.project?.id);
        setValue('invoice_id', payment[0].invoice_id);
        setValue('amount_paid', payment[0].amount);

        // Track form data population
        mixpanel.track('Super Admin Edit Payment Form Data Populated', {
          user_id: userId,
          payment_id: id,
          builder_id: payment[0]?.invoice?.builder?.id,
          company_id: payment[0]?.invoice?.company?.id,
          project_id: payment[0]?.invoice?.project?.id,
          invoice_id: payment[0].invoice_id,
          amount: payment[0].amount,
          timestamp: new Date().toISOString()
        });
      }
      setLoading(false);
    }
  }, [
    payment,
    usersLoading,
    companiesLoading,
    invoicesLoading,
    isProjectsLoading,
    paymentLoading,
    setValue,
    userId,
    id
  ]);

  const handleFormSubmit = (data) => {
    mixpanel.track('Super Admin Edit Payment Form Submitted', {
      user_id: userId,
      payment_id: id,
      updated_builder_id: data.builder_id,
      updated_company_id: data.company_id,
      updated_project_id: data.project_id,
      updated_invoice_id: data.invoice_id,
      updated_amount: data.amount_paid,
      timestamp: new Date().toISOString()
    });

    onSubmit(data);
  };

  const handleFormCancel = () => {
    mixpanel.track('Super Admin Edit Payment Form Cancelled', {
      user_id: userId,
      payment_id: id,
      timestamp: new Date().toISOString()
    });

    onClose();
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  /* 
    Helper method calls
  */
  const transformedData =
    users?.data?.map((item) => ({
      value: item.id,
      label: item.primery_user?.name?.trim()
    })) || [];

  const transformedDataCompany =
    companies?.map((item) => ({
      value: item.id,
      label: item.company_name?.trim()
    })) || [];

  const transformedDataInvoices =
    invoices?.map((item) => ({
      value: item.id,
      label: item.invoice_number?.trim()
    })) || [];

  const transformedDataProjects =
    projects?.map((item) => ({
      value: item.id,
      label: item.name?.trim()
    })) || [];

  /* 
    JSX
  */
  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label={'Builder Name'}
            register={register}
            register_key={'builder_id'}
            type={'dropdown'}
            defaultValuePre={'Select builder'}
            options={[{ value: '', label: 'Select builder' }, ...transformedData]}
          />

          <FormInput
            width={48}
            label={'Company Name'}
            register={register}
            register_key={'company_id'}
            type={'dropdown'}
            defaultValuePre={'Select company'}
            options={[{ value: '', label: 'Select company' }, ...transformedDataCompany]}
          />
        </div>

        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label={'Project name'}
            register_key={'project_id'}
            register={register}
            type={'dropdown'}
            defaultValuePre={'Select project'}
            options={[{ value: '', label: 'Select project' }, ...transformedDataProjects]}
          />

          <FormInput
            width={48}
            label={'Invoice'}
            register_key={'invoice_id'}
            register={register}
            type={'dropdown'}
            defaultValuePre={'Select invoice'}
            options={[{ value: '', label: 'Select invoice' }, ...transformedDataInvoices]}
          />
        </div>

        <div className="flex justify-between items-center w-full">
          <FormInput
            width={48}
            label={'Amount paid'}
            register_key={'amount_paid'}
            register={register}
            type={'text'}
            defaultValuePre={data?.amount_paid || ''}
          />
        </div>
      </div>

      <div className="w-full flex justify-end items-center space-x-4 mt-8">
        <Button type="reset" onClick={handleFormCancel} displayType="secondary">
          Cancel
        </Button>
        <Button onClick={handleSubmit(handleFormSubmit)} type="submit">
          Save
        </Button>
      </div>
    </>
  );
};
